import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import type { InventoryItem, InventoryMovement } from "@dentacore/shared-types";

import { dayLabel, groupByDay } from "../patients/by-day";
import { formatMoney, minorUnitsPerUnit, parseMoney } from "../patients/money";
import { describeNumberError, NumberField } from "../ui/number-field";
import { Pager, useFitPages } from "../ui/paging";
import { RowMenu } from "../ui/popovers";
import type { RowMenuItem } from "../ui/popovers";
import {
  AMOUNT,
  available,
  COUNT,
  countText,
  isLow,
  otherUnitText,
  kindLabel,
  packText,
  plural,
  toStored,
  unitOf,
  USES,
  usesPerPack,
} from "./stock";
import styles from "./inventory.module.css";
import { RequiredMark } from "../ui/required-mark";
import { useBranchCurrency } from "../api/use-branch-currency";

export type ItemTab = "stock" | "history";

/**
 * **What the quick ring may ask this panel to start** (STORY-221, QR8): the
 * same form the panel's own button or ⋯ entry opens — Add, Remove and Set low
 * threshold on the Stock tab, Change price and Retire's warning from the ⋯.
 * Nothing is saved by the asking; the person still types and confirms.
 * `seq` makes asking twice for the same thing count twice.
 */
export type ItemAsk = "add" | "remove" | "low" | "price" | "retire";
export interface ItemAskRequest {
  what: ItemAsk;
  seq: number;
}

/**
 * One item, opened beside the list — **the panel is back** (IC6, 2026-10-06).
 *
 * IC3 had a click open a small card with three actions and moved History,
 * Suppliers and the rest to a ⋯ on the row. Anthony, after using it: *"I dont
 * like that i have to click on 3 buttons to access the remaining issues, the
 * tab should open like before."* So a click opens this panel with its tabs
 * again, and **IC3's three actions are its first tab, alone**:
 *
 *  - **Stock** — what is left, the item's alert line in words, and *Add*,
 *    *Remove*, *Set low threshold*. Nothing else (IC3 stands for the tab).
 *  - **History** — what moved, with its count (IV6).
 *  - **See suppliers** — a link into Supply (STORY-189a), where who sells it now lives.
 *
 * Change price, Opened one and Retire are on the panel's **⋯**, as Change
 * price and Retire were before IC3. The figures stay in columns on the list.
 *
 * **The controls split by permission, because the endpoints do** (TP17):
 * Add, Remove and Opened one are the chairside half; the threshold, the price
 * and retiring are setting stock up. Hiding a control spares somebody a button
 * that would be refused; the server refuses regardless.
 */
export function ItemPanel({
  item,
  movements,
  canMove,
  canSetUp,
  busy,
  tab,
  onTab,
  onClose,
  onMove,
  onLowAt,
  onWantToBuy,
  onOpenUnit,
  onRetire,
  onRestore,
  onPrice,
  onEstimate,
  onEdit,
  supplyHref,
  locations,
  ask = null,
  onAsked,
}: {
  /** A form the quick ring asked for (STORY-221); cleared through `onAsked` once started. */
  ask?: ItemAskRequest | null;
  onAsked?: () => void;
  item: InventoryItem;
  /** Null while it is being read. */
  movements: readonly InventoryMovement[] | null;
  canMove: boolean;
  canSetUp: boolean;
  busy: boolean;
  tab: ItemTab;
  onTab: (tab: ItemTab) => void;
  onClose: () => void;
  /** Signed, in uses. Resolves true once it landed, so the field clears only then. */
  onMove: (delta: number) => Promise<boolean>;
  onLowAt: (lowAt: number) => Promise<boolean>;
  /** Mark or unmark it to buy (Emile, 2026-10-17). */
  onWantToBuy: (want: boolean) => void;
  onOpenUnit: () => void;
  onRetire: () => void;
  onRestore: () => void;
  /** Resolves true once the new price landed (IP-D1), so the form closes only then. */
  onPrice: (priceCents: number) => Promise<boolean>;
  /** A bulk item's estimate of uses a pack (MC-D4). Resolves true once it landed. */
  onEstimate: (estimatedUses: number) => Promise<boolean>;
  /** *Edit* (OQ-D18): name, pack, full shelf, uses a pack. Resolves true once it landed. */
  onEdit: (patch: ItemEdit) => Promise<boolean>;
  /**
   * Where *See suppliers* goes — set only when the clinic has Supply, whose
   * Suppliers moved out of this panel into their own screen (STORY-189a, MOD-D21).
   */
  supplyHref: string | null;
  /** The switch between this item's copies at each location shown. */
  locations?: React.ReactNode;
}): React.JSX.Element {
  // The page keys this panel by item, so neither form carries over.
  const [confirming, setConfirming] = useState(false);
  const [pricing, setPricing] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [editing, setEditing] = useState(false);
  const currency = useBranchCurrency();

  /**
   * The ring's Change price and Retire (STORY-221) set exactly what the ⋯
   * entries below set, under the same conditions — so Retire still stops at
   * its warning and waits for *Retire it*. Add, Remove and the threshold are
   * the Stock tab's, which takes them itself.
   */
  useEffect(() => {
    if (ask === null || (ask.what !== "price" && ask.what !== "retire")) return;
    if (item.isActive && canSetUp) {
      setPricing(ask.what === "price");
      setEstimating(false);
      setConfirming(ask.what === "retire");
    }
    onAsked?.();
  }, [ask, item.isActive, canSetUp, onAsked]);

  const menu: RowMenuItem[] = [];
  // **Edit** (OQ-D18): a typo in the name, or a supplier's new box size, no
  // longer means retiring the item and losing its history.
  if (item.isActive && canSetUp) {
    menu.push({
      label: "Edit",
      disabled: busy,
      onSelect: () => {
        setConfirming(false);
        setPricing(false);
        setEstimating(false);
        setEditing(true);
      },
      testId: `inventory-edit-${item.id}`,
    });
  }
  if (item.isActive && canSetUp) {
    menu.push({
      label: "Change price",
      disabled: busy,
      onSelect: () => {
        setConfirming(false);
        setEstimating(false);
        setPricing(true);
      },
      testId: `inventory-price-${item.id}`,
    });
  }
  // The second exception to "no editing an item after it is added" (MC-D4,
  // after IP-D8's price): the estimate is a guess, and guesses get corrected.
  if (item.isActive && canSetUp && item.kind === "BULK") {
    menu.push({
      label: "Change uses estimate",
      disabled: busy,
      onSelect: () => {
        setConfirming(false);
        setPricing(false);
        setEstimating(true);
      },
      testId: `inventory-estimate-${item.id}`,
    });
  }
  // A bulk item moves only when one is opened (TP7) — the chairside action.
  if (item.isActive && canMove && item.kind === "BULK") {
    menu.push({
      label: "Opened one",
      disabled: busy,
      onSelect: onOpenUnit,
      testId: `inventory-open-${item.id}`,
    });
  }
  if (item.isActive && canSetUp) {
    menu.push({
      label: "Retire",
      danger: true,
      disabled: busy,
      onSelect: () => {
        setPricing(false);
        setEstimating(false);
        setConfirming(true);
      },
      testId: `inventory-retire-${item.id}`,
    });
  }

  const trays = item.usedByProcedureCount;

  return (
    <aside
      className={styles.panel}
      aria-label={item.name}
      data-testid="inventory-item-panel"
      data-item={item.id}
    >
      <div className={styles.panelHead}>
        <h2 className={styles.panelTitle}>{item.name}</h2>
        {menu.length > 0 && (
          <RowMenu
            label={`More for ${item.name}`}
            items={menu}
            testId={`inventory-more-${item.id}`}
          />
        )}
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label="Close"
          data-testid="inventory-panel-close"
        >
          ×
        </button>
      </div>
      <p className={styles.panelSub}>
        {kindLabel(item.kind)} · {packText(item)} ·{" "}
        {item.priceNeeded ? (
          // No price yet (OQ-D7): set it with Change price.
          <span className={styles.priceNeeded} data-testid={`inventory-price-needed-${item.id}`}>
            price needed
          </span>
        ) : (
          <span data-testid={`inventory-pack-price-${item.id}`}>
            {formatMoney(item.priceCents, currency)} a pack
          </span>
        )}
        {item.estimatedUses !== null && (
          <span data-testid={`inventory-estimated-uses-${item.id}`}>
            {" "}
            · about {plural(item.estimatedUses, "use")} in each
          </span>
        )}
      </p>
      {locations !== undefined && <div className={styles.panelLocations}>{locations}</div>}

      {editing && (
        <EditForm
          item={item}
          busy={busy}
          onSave={(patch) =>
            onEdit(patch).then((landed) => {
              if (landed) setEditing(false);
              return landed;
            })
          }
          onCancel={() => setEditing(false)}
        />
      )}
      {pricing && (
        <PriceForm
          item={item}
          currency={currency}
          busy={busy}
          onSave={(cents) =>
            onPrice(cents).then((landed) => {
              if (landed) setPricing(false);
              return landed;
            })
          }
          onCancel={() => setPricing(false)}
        />
      )}
      {estimating && item.estimatedUses !== null && (
        <EstimateForm
          item={item}
          estimate={item.estimatedUses}
          busy={busy}
          onSave={(uses) =>
            onEstimate(uses).then((landed) => {
              if (landed) setEstimating(false);
              return landed;
            })
          }
          onCancel={() => setEstimating(false)}
        />
      )}
      {confirming && (
        <div className={styles.warnBox}>
          {/* Warned with the count, never refused — the `futureAppointmentCount`
              idiom. Retiring a material a procedure still lists is the admin's
              judgement. */}
          <p>
            {trays > 0
              ? `${plural(trays, "procedure")} here still ${trays === 1 ? "lists" : "list"} ${item.name}. Retiring it leaves ${trays === 1 ? "it" : "those"} asking for something you no longer stock.`
              : `Retire ${item.name}? Its history stays, and you can bring it back.`}
          </p>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.danger}
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                onRetire();
              }}
              data-testid={`inventory-retire-confirm-${item.id}`}
            >
              Retire it
            </button>
            <button type="button" className={styles.ghost} onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
        </div>
      )}

      <div
        className={styles.panelTabs}
        role="tablist"
        aria-label={`${item.name}, stock or history`}
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === "stock"}
          className={tab === "stock" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
          onClick={() => onTab("stock")}
          data-testid="inventory-tab-stock"
        >
          Stock
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "history"}
          className={tab === "history" ? `${styles.tab} ${styles.tabOn}` : styles.tab}
          onClick={() => onTab("history")}
          data-testid={`inventory-history-${item.id}`}
        >
          History
          {movements !== null && <span className={styles.tabCount}>{movements.length}</span>}
        </button>
      </div>

      {tab === "stock" ? (
        <StockTab
          item={item}
          canMove={canMove}
          canSetUp={canSetUp}
          busy={busy}
          onMove={onMove}
          onLowAt={onLowAt}
          onWantToBuy={onWantToBuy}
          onRestore={onRestore}
          ask={ask !== null && ask.what !== "price" && ask.what !== "retire" ? ask : null}
          onAsked={onAsked}
        />
      ) : (
        <History itemId={item.id} movements={movements} />
      )}
      {supplyHref !== null && (
        <Link to={supplyHref} data-testid={`inventory-see-suppliers-${item.id}`}>
          See suppliers
        </Link>
      )}
    </aside>
  );
}

type Action = "add" | "remove" | "low";

/**
 * What is left and the three things that change it (IC3). Each action is one
 * number, with no reason (IC5).
 *
 * **Typed in packs or uses, starting on packs** (QA4-D2, 2026-10-18). Emile:
 * *"if I set the gloves to be in bulk, one pack contains 100 gloves — adding
 * one means adding one pack, not one glove; the low threshold should be on the
 * box"*. What is stored does not change — a counted item in uses, a bulk item
 * in packs (FX22) — and the switch converts what is typed. Shown only where a
 * pack holds more than one use.
 */
function StockTab({
  item,
  canMove,
  canSetUp,
  busy,
  onMove,
  onLowAt,
  onWantToBuy,
  onRestore,
  ask,
  onAsked,
}: {
  item: InventoryItem;
  canMove: boolean;
  canSetUp: boolean;
  busy: boolean;
  onMove: (delta: number) => Promise<boolean>;
  onLowAt: (lowAt: number) => Promise<boolean>;
  onWantToBuy: (want: boolean) => void;
  onRestore: () => void;
  /** Add, Remove or Set low threshold, asked for from the quick ring (STORY-221). */
  ask: ItemAskRequest | null;
  onAsked: (() => void) | undefined;
}): React.JSX.Element {
  const [action, setAction] = useState<Action | null>(null);
  const [typed, setTyped] = useState("");
  const [typedIn, setTypedIn] = useState<"pack" | "use">("pack");

  const low = isLow(item);
  const count = item.quantityOnHand;
  /** Packs for a bulk item, uses for the rest (FX22). */
  const unit = unitOf(item);
  const free = available(item);
  const perPack = usesPerPack(item);
  const hasPacks = perPack > 1;
  /** What the field is in: packs or uses where a pack holds several, else the stored unit. */
  const inUnit: "pack" | "use" = hasPacks ? typedIn : unit;

  const pick = (next: Action): void => {
    setAction(next);
    if (next === "low") {
      // The threshold opens in packs when it is a whole number of them.
      const inPacks = hasPacks && (unit === "pack" || item.lowAt % perPack === 0);
      setTypedIn(inPacks ? "pack" : "use");
      setTyped(String(inPacks && unit === "use" ? item.lowAt / perPack : item.lowAt));
      return;
    }
    setTypedIn("pack");
    setTyped("");
  };

  /**
   * The ring's Add, Remove and Low threshold (STORY-221) press the same button
   * below, under the same conditions it is drawn on: the form opens, and the
   * person types the number and saves it as ever (IC5).
   */
  useEffect(() => {
    if (ask === null || (ask.what !== "add" && ask.what !== "remove" && ask.what !== "low")) return;
    const drawn =
      item.isActive && (ask.what === "low" ? canSetUp && item.kind !== "REUSABLE" : canMove);
    if (drawn) pick(ask.what);
    onAsked?.();
    // `pick` is rebuilt every render; the request is what this answers.
  }, [ask]);

  const amount = Number(typed);
  const stored =
    typed.trim() === "" || !Number.isInteger(amount) ? null : toStored(item, amount, inUnit);
  const valid = stored !== null && amount >= (action === "low" ? 0 : 1);
  /** What the typed number comes to in the stored unit, when the two differ. */
  const comesTo =
    stored === null || inUnit === unit || amount === 0 ? null : countText(item, stored);
  const cannotSplit =
    typed.trim() !== "" && Number.isInteger(amount) && amount > 0 && stored === null;

  const save = (): void => {
    if (!valid || action === null || stored === null) return;
    const write = action === "low" ? onLowAt(stored) : onMove(action === "add" ? stored : -stored);
    void write.then((landed) => {
      if (landed) {
        setAction(null);
        setTyped("");
      }
    });
  };

  /**
   * The alert line **in words a person can act on** (IC8). "Low at 25 uses — a
   * quarter of a full shelf" was the first try and read as a riddle: it named
   * neither what happens at 25 nor where the 25 came from. Every item now
   * carries its own number (IC7), so there is only the number to say.
   */
  const line =
    item.kind === "REUSABLE"
      ? "Never used up, so never low"
      : `Alert when ${countText(item, item.lowAt)}${
          otherUnitText(item, item.lowAt) === null
            ? ""
            : ` (${otherUnitText(item, item.lowAt) ?? ""})`
        } or fewer ${item.lowAt === 1 ? "is" : "are"} left after what is booked`;

  return (
    <div className={styles.details}>
      <p className={styles.figure}>
        {/* Negative is shown as it is. A stale figure is only fixable by
            somebody who can see that it is wrong (TP14). */}
        <b
          className={low ? `${styles.figureNumber} ${styles.warmWord}` : styles.figureNumber}
          data-testid={`inventory-count-${item.id}`}
        >
          {count}
        </b>{" "}
        <span>
          {unit}
          {Math.abs(count) === 1 ? "" : "s"} on the shelf
          {otherUnitText(item, count) !== null && ` · ${otherUnitText(item, count) ?? ""}`}
        </span>
      </p>
      {/* **What the diary has promised** (FX26), and what that leaves. */}
      {item.bookedQuantity > 0 && (
        <p className={styles.figureNote} data-testid={`inventory-booked-${item.id}`}>
          {countText(item, item.bookedQuantity)} booked for procedures ·{" "}
          {free < 0 ? (
            <span className={styles.warmWord}>
              {countText(item, -free)} short for what is booked
            </span>
          ) : (
            `${countText(item, free)} free`
          )}
        </p>
      )}
      <p className={styles.figureNote} data-testid={low ? "inventory-low" : undefined}>
        {!item.isActive ? (
          "Retired. Its history stays."
        ) : (
          <>
            {low && <span className={styles.warmWord}>Running low · </span>}
            <span data-testid={`inventory-low-at-${item.id}`}>{line}</span>
          </>
        )}
      </p>

      {!item.isActive ? (
        canSetUp && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={onRestore}
              data-testid={`inventory-restore-${item.id}`}
            >
              Bring back
            </button>
          </div>
        )
      ) : (
        <>
          <div className={styles.actions} role="group" aria-label="Change the count">
            {canMove && (
              <>
                <button
                  type="button"
                  className={styles.ghost}
                  aria-pressed={action === "add"}
                  disabled={busy}
                  onClick={() => pick("add")}
                  data-testid={`inventory-add-${item.id}`}
                >
                  Add
                </button>
                <button
                  type="button"
                  className={styles.ghost}
                  aria-pressed={action === "remove"}
                  disabled={busy}
                  onClick={() => pick("remove")}
                  data-testid={`inventory-remove-${item.id}`}
                >
                  Remove
                </button>
              </>
            )}
            {canSetUp && item.kind !== "REUSABLE" && (
              <button
                type="button"
                className={styles.ghost}
                aria-pressed={action === "low"}
                disabled={busy}
                onClick={() => pick("low")}
                data-testid={`inventory-low-${item.id}`}
              >
                Set low threshold
              </button>
            )}
          </div>
          {/* **Its own flag, not the cart** (WB-D1): "we'll need this", said by
              whoever is at the shelf — the same people who can add to the cart. It
              floats the item up the catalogue until an order for it is placed. Its
              own row, outside "Change the count": starring moves no count (IC3). */}
          {canMove && (
            <div className={styles.actions}>
              <button
                type="button"
                className={item.wantToBuy ? `${styles.ghost} ${styles.wantOn}` : styles.ghost}
                aria-pressed={item.wantToBuy}
                disabled={busy}
                onClick={() => onWantToBuy(!item.wantToBuy)}
                data-testid={`inventory-want-${item.id}`}
              >
                <span aria-hidden="true">{item.wantToBuy ? "★" : "☆"}</span> Want to buy
              </button>
            </div>
          )}
          {item.wantToBuy && (
            <p className={styles.figureNote} data-testid={`inventory-wanted-${item.id}`}>
              Starred at the top of the catalogue until it is ordered.
            </p>
          )}

          {action !== null && (
            <form
              className={styles.panelForm}
              onSubmit={(event) => {
                event.preventDefault();
                save();
              }}
            >
              {hasPacks && (
                <div
                  className={styles.unitSwitch}
                  role="radiogroup"
                  aria-label="Count in"
                  data-testid={`inventory-unit-${item.id}`}
                >
                  {(["pack", "use"] as const).map((u) => (
                    <button
                      key={u}
                      type="button"
                      role="radio"
                      aria-checked={typedIn === u}
                      className={typedIn === u ? styles.unitOn : styles.unitOff}
                      onClick={() => setTypedIn(u)}
                      data-testid={`inventory-unit-${u}-${item.id}`}
                    >
                      {u === "pack" ? `Packs of ${String(perPack)}` : "Uses"}
                    </button>
                  ))}
                </div>
              )}
              <NumberField
                label={
                  action === "add"
                    ? `${inUnit === "pack" ? "Packs" : "Uses"} to add`
                    : action === "remove"
                      ? `${inUnit === "pack" ? "Packs" : "Uses"} to take off`
                      : `Alert when this many ${inUnit}s are left`
                }
                className={styles.field}
                labelClassName={styles.fieldLabel}
                inputClassName={styles.input}
                value={typed}
                onChange={setTyped}
                rules={{ ...(action === "low" ? COUNT : AMOUNT), unit: `${inUnit}s` }}
                hint={
                  cannotSplit
                    ? `Counted in whole packs of ${String(perPack)} — type a multiple of ${String(perPack)}.`
                    : comesTo === null
                      ? undefined
                      : `= ${comesTo}`
                }
                autoFocus
                testId={`inventory-amount-${item.id}`}
              />
              <div className={styles.formActions}>
                <button
                  type="submit"
                  className={styles.primary}
                  disabled={busy || !valid}
                  data-testid={`inventory-amount-save-${item.id}`}
                >
                  {action === "add" ? "Add" : action === "remove" ? "Take off" : "Save"}
                </button>
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => {
                    setAction(null);
                    setTyped("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

/** What *Edit* may change (OQ-D18). */
export interface ItemEdit {
  name?: string;
  stockUnit?: string;
  parLevel?: number | null;
  usesPerStockUnit?: number;
}

/**
 * *Edit* (OQ-D18) — the name, what a pack is called, the full shelf and, for an
 * item counted in uses, how many a pack holds. A new pack size keeps the
 * unopened packs as packs; the server re-counts them and records the
 * difference, so the count still equals its movements. Only changed fields go.
 */
function EditForm({
  item,
  busy,
  onSave,
  onCancel,
}: {
  item: InventoryItem;
  busy: boolean;
  onSave: (patch: ItemEdit) => Promise<boolean>;
  onCancel: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(item.name);
  const [pack, setPack] = useState(item.stockUnit);
  const [shelf, setShelf] = useState(item.parLevel === null ? "" : String(item.parLevel));
  const [per, setPer] = useState(String(item.usesPerStockUnit));
  const countedInUses = item.kind !== "BULK";
  const shelfValue = shelf.trim() === "" ? null : Number(shelf);
  const perValue = Number(per);
  const valid =
    name.trim() !== "" &&
    pack.trim() !== "" &&
    (shelfValue === null || (Number.isInteger(shelfValue) && shelfValue >= 0)) &&
    (!countedInUses || (Number.isInteger(perValue) && perValue >= 1));
  const patch: ItemEdit = {
    ...(name.trim() === item.name ? {} : { name: name.trim() }),
    ...(pack.trim() === item.stockUnit ? {} : { stockUnit: pack.trim() }),
    ...(shelfValue === item.parLevel ? {} : { parLevel: shelfValue }),
    ...(countedInUses && perValue !== item.usesPerStockUnit ? { usesPerStockUnit: perValue } : {}),
  };
  return (
    <form
      className={styles.panelForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || Object.keys(patch).length === 0) return;
        void onSave(patch);
      }}
      data-testid={`inventory-edit-form-${item.id}`}
    >
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Name</span>
        <input
          className={styles.input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </label>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>A pack is called</span>
        <input className={styles.input} value={pack} onChange={(e) => setPack(e.target.value)} />
      </label>
      {countedInUses && (
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Uses in one pack</span>
          <input
            className={styles.input}
            inputMode="numeric"
            value={per}
            onChange={(e) => setPer(e.target.value)}
            data-testid={`inventory-edit-per-${item.id}`}
          />
        </label>
      )}
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Full shelf</span>
        <input
          className={styles.input}
          inputMode="numeric"
          value={shelf}
          onChange={(e) => setShelf(e.target.value)}
          placeholder="Not set"
        />
      </label>
      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.primary}
          disabled={busy || !valid || Object.keys(patch).length === 0}
          data-testid={`inventory-edit-save-${item.id}`}
        >
          Save
        </button>
        <button type="button" className={styles.ghost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * *Change price* (IP-D1). A price can change but never be cleared — the field
 * starts on the current one, and an empty or zero amount cannot be saved.
 */
function PriceForm({
  item,
  currency,
  busy,
  onSave,
  onCancel,
}: {
  item: InventoryItem;
  currency: string;
  busy: boolean;
  onSave: (priceCents: number) => Promise<boolean>;
  onCancel: () => void;
}): React.JSX.Element {
  const [typed, setTyped] = useState(() =>
    (item.priceCents / minorUnitsPerUnit(currency)).toString(),
  );
  const cents = parseMoney(typed, currency);
  return (
    <form
      className={styles.panelForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (cents === null) return;
        void onSave(cents);
      }}
    >
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          Price per pack
          <RequiredMark />
        </span>
        <input
          className={styles.input}
          inputMode="decimal"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          aria-invalid={cents === null}
          aria-describedby={cents === null ? `inventory-price-error-${item.id}` : undefined}
          autoFocus
          data-testid={`inventory-price-input-${item.id}`}
        />
        {cents === null && (
          <span className={styles.fieldError} id={`inventory-price-error-${item.id}`} role="alert">
            Type an amount above zero.
          </span>
        )}
      </label>
      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.primary}
          disabled={busy || cents === null}
          data-testid={`inventory-price-save-${item.id}`}
        >
          Save price
        </button>
        <button type="button" className={styles.ghost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * *Change uses estimate* (MC-D4) — about how many uses one pack of a bulk item
 * serves. Starts on the current figure and can never be emptied, the
 * `PriceForm` idiom: a bulk item always carries one.
 */
function EstimateForm({
  item,
  estimate,
  busy,
  onSave,
  onCancel,
}: {
  item: InventoryItem;
  estimate: number;
  busy: boolean;
  onSave: (estimatedUses: number) => Promise<boolean>;
  onCancel: () => void;
}): React.JSX.Element {
  const [typed, setTyped] = useState(String(estimate));
  const invalid = typed.trim() === "" || describeNumberError(typed, USES) !== null;
  return (
    <form
      className={styles.panelForm}
      onSubmit={(event) => {
        event.preventDefault();
        if (invalid) return;
        void onSave(Number(typed));
      }}
    >
      <NumberField
        label="About how many uses in one pack?"
        required
        className={styles.field}
        labelClassName={styles.fieldLabel}
        inputClassName={styles.input}
        value={typed}
        onChange={setTyped}
        rules={USES}
        hint="For costing procedures only — stock still opens a whole pack at a time."
        testId={`inventory-estimate-input-${item.id}`}
      />
      <div className={styles.formActions}>
        <button
          type="submit"
          className={styles.primary}
          disabled={busy || invalid}
          data-testid={`inventory-estimate-save-${item.id}`}
        >
          Save estimate
        </button>
        <button type="button" className={styles.ghost} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** A date line's height, and a movement's. The pager counts on both. */
const DAY_HEAD = 32;
const MOVE_ROW = 44;
const DAY_TAIL = 8;

/**
 * What moved, newest first: **a date line and a little space between days, and
 * no boxes** (IV6). Day containers in the panel were tried on the prototype and
 * read as *"too crowded"*. A day never splits across two pages (PF4).
 */
function History({
  itemId,
  movements,
}: {
  itemId: string;
  movements: readonly InventoryMovement[] | null;
}): React.JSX.Element {
  const days = movements === null ? [] : groupByDay(movements, (row) => row.createdAt);
  const pages = useFitPages({
    keys: days.map((day) => day.key),
    estimate: (index) => DAY_HEAD + MOVE_ROW * (days[index]?.rows.length ?? 1) + DAY_TAIL,
    gap: 0,
    resetOn: itemId,
  });
  const [start, end] = pages.range;

  if (movements === null) return <p className={styles.blank}>Reading&hellip;</p>;
  if (movements.length === 0) return <p className={styles.blank}>Nothing has moved yet.</p>;

  return (
    <div className={styles.history} data-testid={`inventory-ledger-${itemId}`}>
      <div className={styles.historyArea} ref={pages.areaRef}>
        {days.slice(start, end).map((day) => (
          <section key={day.key} className={styles.day} ref={pages.measure(day.key)}>
            <h3 className={styles.dayLabel}>{dayLabel(day.key)}</h3>
            {day.rows.map((movement) => (
              <div key={movement.id} className={styles.move}>
                <span className={movement.quantityDelta > 0 ? styles.movePlus : styles.moveDelta}>
                  {movement.quantityDelta > 0 ? "+" : ""}
                  {movement.quantityDelta}
                </span>
                <span className={styles.moveText}>
                  <span className={styles.moveWhat}>
                    {movement.kind === "CONSUMPTION"
                      ? "Used in a visit"
                      : movement.kind === "OPENED"
                        ? "Opened one"
                        : movement.kind === "RESTOCK"
                          ? "Order arrived"
                          : // No reason means it came from Add or Remove (IC5);
                            // with one, it is a correction somebody explained.
                            movement.reason === null
                            ? movement.quantityDelta > 0
                              ? "Added"
                              : "Removed"
                            : "Corrected"}
                    {movement.reason !== null && ` · ${movement.reason}`}
                  </span>
                  {/* Null once that person has left. The movement survives them,
                      so the ledger keeps the fact even when it loses the name. */}
                  <span className={styles.moveWho}>
                    {movement.createdByName ?? "someone since departed"}
                  </span>
                </span>
              </div>
            ))}
          </section>
        ))}
      </div>
      <div className={styles.cardFoot}>
        <Pager
          page={pages.page}
          count={pages.pages.length}
          onPage={pages.setPage}
          from={start + 1}
          to={end}
          total={days.length}
          noun="days"
          testId="inventory-history-pager"
        />
      </div>
    </div>
  );
}
